import {
  useGetUserDatumByIdQuery,
  useUserDatumUpdatesMutation,
} from "@codegen/index";
import { UpdateUserDatumByIdInput } from "@codegen/schema";
import { Button } from "@components/button";
import {
  FormContainer,
  OnboardingContainer,
  OnboardingFormField,
} from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { OnboardingPageType } from "@pages/onboarding/types";
import { X } from "@phosphor-icons/react";
import { useDebounceCallback } from "@react-hook/debounce";
import { useLogError } from "@utils/error";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { z } from "zod";
import { FormLoading } from "./loading";

const nameSchema = z.object({
  firstName: z.string().min(1, "Please provide a first name"),
  lastName: z.string().min(1, "Please provide a last name"),
  middleName: z.string().optional(),
});
export const formSchema = z.object({
  hasOtherNames: z.literal("yes"),
  previousNames: nameSchema.array().nonempty(),
});
export const previousNamesFormSchemaDetails = z.discriminatedUnion(
  "hasOtherNames",
  [formSchema, z.object({ hasOtherNames: z.literal("no") })]
);

const defaultName = { firstName: "", lastName: "", middleName: "" };
export type PreviousNamesFormType = z.infer<
  typeof previousNamesFormSchemaDetails
>;
export type NameType = {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
};
type PreviousNamesType = NameType[];
export const PreviousNamesForm: React.FC<
  OnboardingPageType & { otherNames?: PreviousNamesType; refetch: () => void }
> = ({
  contact,
  onSubmit,
  autoSave,
  onBack,
  otherNames,
  refetch,
  onSkipForNow,
  exhibit,
  moduleProgress,
  currentStep,
  showSkipForNow,
  hideExitButton,
}) => {
  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const methods = useForm({
    defaultValues: {
      hasOtherNames: otherNames && otherNames.length > 0 ? "yes" : "no",
      previousNames: otherNames as PreviousNamesType,
    },
    resolver: zodResolver(previousNamesFormSchemaDetails),
  });
  const { isReadOnly } = useReadOnlyMode();

  const { formState, control } = methods;
  const { fields, append, remove } = useFieldArray<
    PreviousNamesFormType,
    "previousNames",
    "id"
  >({
    // @ts-ignore
    control,
    name: "previousNames",
  });
  const logError = useLogError();
  const [updateUserData, { loading: saving }] = useUserDatumUpdatesMutation();
  const hasOtherNames = useWatch({
    control,
    name: "hasOtherNames",
  });

  const watchedValues = useWatch({
    control,
  });

  const performMutation = useCallback(
    async (data: PreviousNamesFormType) => {
      const { errors: updateUserError } = await updateUserData({
        variables: {
          input: {
            id: contact.id,
            userDatumPatch: {
              // Clear otherNames when "no" is selected, otherwise save the names
              otherNames:
                data.hasOtherNames === "yes"
                  ? JSON.stringify(data.previousNames)
                  : null,
            },
          } as UpdateUserDatumByIdInput,
        },
      });

      if (updateUserError != null) {
        throw updateUserError;
      }
    },
    [updateUserData, contact.id]
  );

  const handleSave = useCallback(
    async (data: PreviousNamesFormType) => {
      const savePromise = (async () => {
        try {
          setStatus("loading");
          await performMutation(data);
          refetch();
          setStatus("success");
          autoSave(data);
        } catch (exception) {
          setStatus("error");
          logError(exception, {
            message: "Error saving user data",
          });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [performMutation, refetch, autoSave, logError]
  );

  const debouncedSave = useDebounceCallback(handleSave, 300);

  useEffect(() => {
    if (Object.keys(watchedValues).length > 0) {
      debouncedSave(watchedValues);
    }
  }, [watchedValues, debouncedSave]);

  const doSubmit = useCallback(
    async (data: PreviousNamesFormType) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setStatus("loading");
        await performMutation(data);
        refetch();
        setStatus("success");
        onSubmit(data);
      } catch (exception) {
        setStatus("error");
        logError(exception, {
          message: "Error saving user data",
        });
      }
    },
    [performMutation, refetch, onSubmit, logError]
  );

  useEffect(() => {
    if (hasOtherNames === "yes" && fields.length < 1) {
      append(defaultName);
    }
  }, [hasOtherNames, fields, append]);

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={exhibit.name}
        subtitle="Have you used any other names?"
        progress={moduleProgress}
        currentStep={currentStep}
        onSubmit={methods.handleSubmit(doSubmit)}
        exhibit={exhibit}
        name="previous-names-form"
        onBack={onBack}
        onSkipForNow={onSkipForNow}
        isLoading={saving}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer
          status={status}
          errorMessage="There was a problem saving your previous names."
        >
          <OnboardingFormField
            name="hasOtherNames"
            type="radio"
            radioOptions={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
            disabled={isReadOnly}
          />

          {hasOtherNames === "yes" && (
            <div className="flex flex-col gap-3">
              <p className="mt-3">
                Please provide them if <b>yes</b>
              </p>
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="border rounded bg-white py-6 px-3 flex gap-3 relative"
                >
                  <div className="w-1/3">
                    <OnboardingFormField
                      name={`previousNames.${index}.firstName`}
                      label="First Name (Given Name)"
                      placeholder="First Name"
                      required={true}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="w-1/3">
                    <OnboardingFormField
                      name={`previousNames.${index}.middleName`}
                      label="Middle Name"
                      description="Optional"
                      placeholder="Middle Name"
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="w-1/3">
                    <OnboardingFormField
                      name={`previousNames.${index}.lastName`}
                      label="Last Name (Family Name)"
                      placeholder="Last Name"
                      required={true}
                      disabled={isReadOnly}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="absolute top-0 right-0 p-2"
                    disabled={isReadOnly}
                    aria-label="Remove previous name"
                    data-testid={`remove-previous-name-${index}`}
                  >
                    <X />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="link"
                className="self-start text-accent underline px-0"
                onClick={() => append(defaultName)}
                disabled={isReadOnly}
              >
                Add more
              </Button>
            </div>
          )}
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export const PreviousNamesFormController: React.FC<OnboardingPageType> = (
  props
) => {
  const {
    data: userData,
    refetch,
    error,
    loading,
  } = useGetUserDatumByIdQuery({
    variables: {
      id: props.contact.id,
    },
  });
  const logError = useLogError();
  const otherNames = React.useMemo(() => {
    if (userData?.userDatumById?.otherNames) {
      return JSON.parse(userData?.userDatumById?.otherNames);
    }
    return undefined;
  }, [userData]);

  if (error) {
    logError(error, { contactId: props.contact.id });
  }

  if (loading) {
    return <FormLoading />;
  }

  return (
    <PreviousNamesForm {...props} otherNames={otherNames} refetch={refetch} />
  );
};

export default PreviousNamesFormController;
